/**
 * Ajustes → Tipologías de gasto: catálogo configurable (mismo patrón que
 * Líneas de servicio). El código es inmutable; las tipologías se desactivan,
 * nunca se borran (los gastos las referencian). La marcada por defecto es el
 * fallback del OCR de facturas recibidas.
 */

import { useEffect, useState, type FormEvent } from 'react'
import { Plus, Pencil, Loader2, CircleCheck, CircleSlash, Star } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../../lib/api'
import { toast } from '../../lib/dialogs'
import Modal from '../../components/ui/Modal'
import { invalidateExpenseCategoriesCache } from '../../lib/expense-categories'
import { type ExpenseCategoryDefDto, type ExpenseCategoryListResponse } from '@toga/shared'

export default function ExpenseCategories() {
  const { t } = useTranslation(['settings', 'common'])
  const [items, setItems] = useState<ExpenseCategoryDefDto[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<ExpenseCategoryDefDto | null>(null)
  const [showForm, setShowForm] = useState(false)

  const reload = async () => {
    setError(null)
    try {
      const resp = await api.get<ExpenseCategoryListResponse>('/expense-categories')
      setItems(resp.items)
      invalidateExpenseCategoriesCache()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('expenseCategories.errors.load'))
    }
  }

  useEffect(() => {
    void reload()
  }, [])

  const onToggleActive = async (s: ExpenseCategoryDefDto) => {
    try {
      await api.patch(`/expense-categories/${s.id}`, { active: !s.active })
      await reload()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('expenseCategories.errors.save'))
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-serif font-semibold t-primary">
            {t('expenseCategories.title')}
          </h2>
          <p className="t-muted text-sm mt-1">{t('expenseCategories.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={() => {
            setEditing(null)
            setShowForm(true)
          }}
          className="px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga transition-all flex items-center gap-1.5 shrink-0"
        >
          <Plus size={16} /> {t('expenseCategories.newButton')}
        </button>
      </header>

      {error && (
        <div className="text-sm rounded-lg px-3 py-2 border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30">
          {error}
        </div>
      )}

      <div className="surface overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider t-faint border-b border-base">
              <th className="px-4 py-3 font-medium">{t('expenseCategories.table.name')}</th>
              <th className="px-4 py-3 font-medium">{t('expenseCategories.table.default')}</th>
              <th className="px-4 py-3 font-medium">{t('expenseCategories.table.status')}</th>
              <th className="px-4 py-3 font-medium text-right">{t('expenseCategories.table.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {items === null ? (
              <tr>
                <td colSpan={4} className="px-4 py-10 text-center t-muted">
                  <Loader2 size={18} className="inline animate-spin mr-2" />
                  {t('expenseCategories.loading')}
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-10 text-center t-muted">
                  {t('expenseCategories.empty')}
                </td>
              </tr>
            ) : (
              items.map((s) => (
                <tr key={s.id} className="border-b border-base/50 last:border-b-0 hover:bg-hover/50">
                  <td className="px-4 py-3 t-primary font-medium">
                    {s.name}
                    {s.description && (
                      <span className="block text-xs t-faint truncate max-w-[320px]">
                        {s.description}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {s.isDefault && (
                      <Star size={14} className="text-amber-600 dark:text-amber-300" />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {s.active ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-300">
                        <CircleCheck size={14} /> {t('expenseCategories.status.active')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs t-muted">
                        <CircleSlash size={14} /> {t('expenseCategories.status.inactive')}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(s)
                          setShowForm(true)
                        }}
                        title={t('expenseCategories.actions.edit')}
                        className="w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted hover:text-toga-700 dark:hover:text-toga-200"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => void onToggleActive(s)}
                        title={
                          s.active
                            ? t('expenseCategories.actions.deactivate')
                            : t('expenseCategories.actions.activate')
                        }
                        className="w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted hover:text-amber-700 dark:hover:text-amber-300"
                      >
                        {s.active ? <CircleSlash size={14} /> : <CircleCheck size={14} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <ExpenseCategoryFormModal
          initial={editing}
          onClose={() => setShowForm(false)}
          onSaved={async () => {
            setShowForm(false)
            await reload()
          }}
        />
      )}
    </div>
  )
}

function ExpenseCategoryFormModal({
  initial,
  onClose,
  onSaved,
}: {
  initial: ExpenseCategoryDefDto | null
  onClose: () => void
  onSaved: () => void | Promise<void>
}) {
  const { t } = useTranslation(['settings', 'common'])
  const isEdit = initial !== null
  const [name, setName] = useState(initial?.name ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [isDefault, setIsDefault] = useState(initial?.isDefault ?? false)
  const [active, setActive] = useState(initial?.active ?? true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        isDefault,
        active,
      }
      if (isEdit && initial) {
        await api.patch(`/expense-categories/${initial.id}`, payload)
      } else {
        await api.post('/expense-categories', payload)
      }
      await onSaved()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as
          | { error?: string; issues?: { fieldErrors?: Record<string, string[]> } }
          | undefined
        const fieldErr = body?.issues?.fieldErrors
        const firstFieldErr = fieldErr ? Object.values(fieldErr).flat().find(Boolean) : undefined
        setError(firstFieldErr ?? body?.error ?? t('expenseCategories.errors.save'))
      } else {
        setError(t('expenseCategories.errors.network'))
      }
      setSubmitting(false)
    }
  }

  const inputCls =
    'w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60'

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? t('expenseCategories.modal.editTitle') : t('expenseCategories.modal.newTitle')}
      maxWidth="max-w-xl"
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-sm t-secondary mb-1.5">
            {t('expenseCategories.form.name')}
          </label>
          <input required value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
        </div>

        <div>
          <label className="block text-sm t-secondary mb-1.5">
            {t('expenseCategories.form.description')}
          </label>
          <input value={description} onChange={(e) => setDescription(e.target.value)} className={inputCls} />
        </div>

        <label className="flex items-center gap-2 text-sm t-secondary">
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
            className="accent-toga-500"
          />
          {t('expenseCategories.form.isDefault')}
        </label>

        <label className="flex items-center gap-2 text-sm t-secondary">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            className="accent-toga-500"
          />
          {t('expenseCategories.form.active')}
        </label>

        {error && (
          <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm bg-[var(--bg-hover)] hover:bg-[var(--bg-input-soft)] t-secondary border border-[var(--border)]"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting || !name.trim()}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga disabled:opacity-50 flex items-center gap-2"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {isEdit ? t('common:actions.save') : t('expenseCategories.modal.createButton')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
